import { getCustomErrorContent } from "@/components/FollowConfirmationModal";
import {
  Body,
  Box,
  ButtCon,
  Button,
  CloudImage,
  Heading,
  Stack,
} from "@biom3/react";
import { MODAL_HEIGHT, MODAL_WIDTH, appConfig } from "@constants";
import { type PropsWithChildren, useEffect, useState } from "react";

const X_LOGO_URL = "/images/quests/x.svg";

export const XModelContentSx = {
  d: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "space-between",
  borderRadius: "base.borderRadius.x10",
  position: "relative",
  overflow: "hidden",
  background: "base.color.brand.2",
  backgroundImage: `url("${appConfig.ASSET_BASE_URL}/images/quests/follow-confirmation-background.svg")`,
  backgroundRepeat: "no-repeat",
  backgroundPosition: "top",
  marginBottom: "base.spacing.x2",
  height: MODAL_HEIGHT,
  width: {
    default: "calc(100dvw - 40px)",
    small: MODAL_WIDTH,
  },
};

export const XModalContainer = ({
  waitingForCompletion,
  onClose,
  ruleId,
  children,
}: PropsWithChildren<{
  waitingForCompletion: boolean;
  onClose: () => void;
  ruleId: string;
}>) => {
  const [xError, setXError] = useState<string | null>(null);

  useEffect(() => {
    // Determine if the user has successfully logged in on the other tab
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // For quests that complete in the login screen immediately close the modal when they return to this tab
        if (localStorage.getItem(ruleId ?? "") === "true") {
          onClose();
        } else {
          const error =
            localStorage.getItem("social_auth_error") ??
            localStorage.getItem("social_quest_error");
          if (error) {
            setXError(error);
          }
        }
      }
    };

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "social_auth_error" && event.newValue) {
        setXError(event.newValue);
      } else if (event.key === "social_quest_error" && event.newValue) {
        setXError(event.newValue);
      }
    };

    window.addEventListener("storage", handleStorageChange);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [ruleId, onClose]);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "base.spacing.x2",
          width: "100%",
          height: "100%",
          mb: "base.spacing.x4",
        }}
      >
        <CloudImage
          imageUrl={`${appConfig.ASSET_BASE_URL}${X_LOGO_URL}`}
          relativeImageSizeInLayout="64px"
          sx={{
            mt: "base.spacing.x32",
            mb: "base.spacing.x8",
            width: "64px",
          }}
        />
        {xError ? (
          <ErrorContent
            error={xError}
            onClose={onClose}
            platform={{ name: "X", questAction: "post" }}
          />
        ) : waitingForCompletion ? (
          <Heading
            size="medium"
            weight="bold"
            sx={{ mx: "base.spacing.x12", textAlign: "center" }}
          >
            Waiting for you to login to X...
          </Heading>
        ) : (
          children
        )}
      </Box>

      <ButtCon
        icon="Close"
        onClick={onClose}
        sx={{
          position: "absolute",
          top: "base.spacing.x4",
          right: "base.spacing.x4",
        }}
        variant="tertiary"
      />
    </>
  );
};

const ErrorContent = ({
  error,
  onClose,
  platform,
}: {
  error: string;
  onClose: () => void;
  platform?: { name: string; questAction: string };
}) => {
  const content = getCustomErrorContent(error, platform);
  return (
    <Stack sx={{ px: "base.spacing.x12", height: "100%" }}>
      <Heading size="medium" weight="bold" sx={{ textAlign: "center" }}>
        {content.heading}
      </Heading>
      <Body sx={{ textAlign: "center", flex: 1 }} size="small">
        {content.body}
      </Body>
      <Button
        variant="primary"
        onClick={onClose}
        sx={{ width: "100%", my: "base.spacing.x1" }}
        size="large"
      >
        {content.ctaLabel}
      </Button>
    </Stack>
  );
};
